import React, { useState, useRef, useEffect } from 'react';
import { Table } from '@tanstack/react-table';
import { SlidersHorizontal, Check, RotateCcw } from 'lucide-react';
import { Button } from './button';

interface ColumnVisibilityDropdownProps<TData> {
  table: Table<TData>;
  onReset: () => void;
}

export function ColumnVisibilityDropdown<TData>({
  table,
  onReset,
}: ColumnVisibilityDropdownProps<TData>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hidableColumns = table
    .getAllColumns()
    .filter((col) => col.getCanHide());

  if (hidableColumns.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2"
      >
        <SlidersHorizontal className="h-4 w-4" />
        <span>Kolumny</span>
      </Button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 min-w-[200px] rounded-md border bg-popover p-1 shadow-md">
          {hidableColumns.map((column) => {
            const isVisible = column.getIsVisible();
            return (
              <button
                key={column.id}
                type="button"
                onClick={() => column.toggleVisibility(!isVisible)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
              >
                <div
                  className={`flex h-4 w-4 items-center justify-center rounded-sm border ${
                    isVisible
                      ? 'bg-primary border-primary'
                      : 'border-muted-foreground/30'
                  }`}
                >
                  {isVisible && (
                    <Check className="h-3 w-3 text-primary-foreground" />
                  )}
                </div>
                <span>
                  {typeof column.columnDef.header === 'string'
                    ? column.columnDef.header
                    : column.id}
                </span>
              </button>
            );
          })}
          <div className="my-1 border-t" />
          <button
            type="button"
            onClick={() => {
              onReset();
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer text-muted-foreground"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span>Przywroc domyslne</span>
          </button>
        </div>
      )}
    </div>
  );
}
